import { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalContent,
  Spinner,
  Tooltip,
} from "@heroui/react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { UnitType, UnitSubtype, UnitDTO } from "shared/types/unit"; // Adjust import paths as necessary
import {
  EditableInputComponent,
  EditableSelectComponent,
} from "components/index";
import { BuildingDTO } from "shared/types/building";
import ButtonNew from "components/buttonNew/buttonNew";
import { Close, Save } from "assets/icons";
import { Lightning } from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";

interface CreateUnitsModalProps {
  isOpen: boolean;
  onClose: any;
  building: BuildingDTO;
  onUnitsCreated: any;
}

const CreateUnitsModal = ({
  isOpen,
  onClose,
  building,
  onUnitsCreated,
}: CreateUnitsModalProps) => {
  const [units, setUnits] = useState<UnitDTO[]>([]);
  const { session } = useSession();
  const [isLoading, setIsLoading] = useState(false); // Loading state

  useEffect(() => {
    if (isOpen && building) {
      initializeUnitsFromBuildingInfo(building.buildingAcquisitionInfo);
    }
  }, [building, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setUnits([]); // Clear units state when the modal is closed
    }
  }, [isOpen]);

  const initializeUnitsFromBuildingInfo = (info: any) => {
    const initialUnits = [];
    // Example for initializing residential units
    for (let i = 0; i < info.acquisitionFileNumResidentialUnits; i++) {
      initialUnits.push({
        unitType: UnitType.Residentieel,
        floor: "",
        unitSubtype: UnitSubtype.Appartement,
        boxNumber: "",
      });
    }

    for (let i = 0; i < info.acquisitionFileNumRetailUnits; i++) {
      initialUnits.push({
        unitType: UnitType.Commercieel,
        floor: "",
        unitSubtype: UnitSubtype.Winkel,
        boxNumber: "",
      });
    }

    for (let i = 0; i < info.acquisitionFileNumOfficeUnits; i++) {
      initialUnits.push({
        unitType: UnitType.Commercieel,
        floor: "",
        unitSubtype: UnitSubtype.Kantoor,
        boxNumber: "",
      });
    }
    // Repeat for retail, office units, etc.
    setUnits(initialUnits as any);
  };

  const handleUnitChange = (index: any, field: any, value: any) => {
    const updatedUnits: any = [...units];
    updatedUnits[index][field] = value;
    setUnits(updatedUnits);
  };

  const isFormValid = () => {
    // Check that every unit has a boxNumber
    return (
      units.every((unit) => unit?.boxNumber && unit?.boxNumber.trim() !== "") &&
      units.every(
        (unit) =>
          unit?.floor != undefined &&
          unit?.floor != null &&
          (unit?.floor as any).trim() !== "",
      )
    );
  };

  const generateUnitIds = () => {
    const updatedUnits = units.map((unit, index) => ({
      ...unit,
      boxNumber: String(index + 1).padStart(2, "0"),
    }));
    setUnits(updatedUnits);
    toast.success(`${units.length} busnummers gegenereerd (01 - ${String(units.length).padStart(2, "0")})`);
  };

  const generateSingleUnitId = (index: number) => {
    const updatedUnits = [...units];
    updatedUnits[index] = {
      ...updatedUnits[index],
      boxNumber: String(index + 1).padStart(2, "0"),
    };
    setUnits(updatedUnits);
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast.error(
        "Zorg ervoor dat alle units een busnummer en een verdieping hebben.",
      );
      return;
    }

    setIsLoading(true); // Start loading

    try {
      const response = await fetchWithAuth(
        `${API_URL}/units/create-all`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ units, buildingId: building.id }),
        },
        session,
      );

      if (response.ok) {
        toast.success("Units successfully created.");
        onUnitsCreated(); // Refresh or update state in parent component
        onClose(); // Close modal and clear state
      } else {
        throw new Error("Failed to create units.");
      }
    } catch (error) {
      console.error("Failed to create units: ", error);
      toast.error("Failed to create units.");
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  const unitTypeOptions = Object.values(UnitType).map((type) => ({
    label: type,
    value: type,
  }));

  const unitSubTypeOptions = Object.values(UnitSubtype).map((subType) => ({
    label: subType,
    value: subType,
  }));

  const unitFloorOptions = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
  ].map((floor) => ({
    label: floor.toString(),
    value: floor.toString(),
  }));

  return (
    <Modal
      scrollBehavior="inside"
      isOpen={isOpen}
      onClose={!isLoading && onClose}
      size="full"
    >
      <ModalHeader>Creating Units for {building?.id}</ModalHeader>
      <ModalContent>
        <ModalBody>
          <div className="flex justify-end gap-2 p-4">
            {isLoading ? (
              <Spinner
                size="md"
                label="Units aan het aanmaken, sluit de modal niet!"
              />
            ) : (
              <>
                <ButtonNew
                  icon={<Save />}
                  label="Opslaan"
                  variant="primaryMoss"
                  onClick={!isLoading && handleSubmit}
                />
                <ButtonNew
                  icon={<Close />}
                  label="Sluiten"
                  variant="primaryBrick"
                  onClick={() => {
                    !isLoading && onClose();
                    setIsLoading(false);
                  }}
                />
              </>
            )}
          </div>
          <div className="overflow-x-auto">
            <div className="mb-4">
              <h2 className="text-lg font-medium">
                Maak mappen aan in Sharepoint {building.id}
                <span className="ml-2 text-sm font-normal text-neutral-secondary-mid">
                  ({units.length} {units.length === 1 ? "unit" : "units"})
                </span>
              </h2>
            </div>
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase">
                <tr>
                  <th scope="col" className="px-3 py-3 text-center w-12">
                    #
                  </th>
                  <th scope="col" className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      <span>Busnummer</span>
                      <Tooltip content={`Genereer alle: 01, 02, ... ${String(units.length).padStart(2, "0")}`}>
                        <button
                          onClick={generateUnitIds}
                          disabled={isLoading || units.length === 0}
                          className="p-1 rounded-md hover:bg-moss-primary/20 text-moss-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          type="button"
                        >
                          <Lightning size={14} weight="fill" />
                        </button>
                      </Tooltip>
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Referentie+
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Verdieping
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Subtype
                  </th>
                </tr>
              </thead>
              <tbody>
                {units.map((unit: UnitDTO, index) => (
                  <tr key={index} className="gap-2 border-b hover:bg-gray-50">
                    <td className="px-3 py-4 text-center">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-moss-primary/10 text-moss-primary font-medium text-xs">
                        {index + 1}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <EditableInputComponent
                          label="Busnummer"
                          editMode={true}
                          isLoading={false}
                          value={unit.boxNumber as string}
                          onChange={(value: any) =>
                            handleUnitChange(index, "boxNumber", value)
                          }
                        />
                        <Tooltip content={`Genereer: ${String(index + 1).padStart(2, "0")}`}>
                          <button
                            onClick={() => generateSingleUnitId(index)}
                            className="p-1.5 rounded-md hover:bg-moss-primary/10 text-neutral-secondary-mid hover:text-moss-primary transition-colors"
                            type="button"
                          >
                            <Lightning size={16} weight="fill" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <EditableInputComponent
                        label="Referentie+"
                        editMode={true}
                        isLoading={false}
                        value={unit.referencePlus as string}
                        onChange={(value: any) =>
                          handleUnitChange(index, "referencePlus", value)
                        }
                      />
                    </td>
                    <td className="px-6 py-4">
                      <EditableSelectComponent
                        label="Verdieping"
                        editMode={true}
                        isLoading={false}
                        options={unitFloorOptions as any}
                        value={unit.floor as any}
                        onChange={(value: any) =>
                          handleUnitChange(index, "floor", value)
                        }
                      />
                    </td>
                    <td className="px-6 py-4">
                      <EditableSelectComponent
                        label="Type"
                        editMode={true}
                        isLoading={false}
                        options={unitTypeOptions}
                        value={unit.unitType as UnitType}
                        onChange={(value: any) =>
                          handleUnitChange(index, "unitType", value)
                        }
                      />
                    </td>
                    <td className="px-6 py-4">
                      <EditableSelectComponent
                        label="Subtype"
                        editMode={true}
                        isLoading={false}
                        options={unitSubTypeOptions}
                        value={unit.unitSubtype as UnitSubtype}
                        onChange={(value: any) =>
                          handleUnitChange(index, "unitSubtype", value)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateUnitsModal;
